<template>
	<div class="finance-page">
		<!-- 无权限保护：仅财务可见 -->
		<div v-if="!isFinance" class="fin-forbidden">
			<i class="el-icon-lock"></i>
			<div class="fb-title">无权访问</div>
			<div class="fb-tip">财务统计仅对财务角色开放。</div>
		</div>

		<template v-else>
			<!-- 顶部筛选栏 -->
			<div class="fin-toolbar">
				<span class="fin-title">财务统计</span>
				<el-select v-model="merchantId" placeholder="选择商户" size="small" class="fin-merchant"
					@change="onQuery">
					<el-option v-for="m in merchants" :key="m.id" :label="m.name + rateSuffix(m.id)" :value="m.id"></el-option>
				</el-select>
				<el-date-picker v-model="dateRange" type="daterange" size="small" unlink-panels
					value-format="yyyy-MM-dd" range-separator="至" start-placeholder="开始日期"
					end-placeholder="结束日期" class="fin-date" @change="onQuery"></el-date-picker>
				<el-button type="primary" size="small" icon="el-icon-search" :loading="overviewLoading"
					@click="onQuery">查询</el-button>
				<el-button size="small" icon="el-icon-download" :disabled="!orders.length"
					@click="exportCsv">导出明细CSV</el-button>
			</div>

			<div v-if="!merchantId" class="fin-empty">请选择商户后查看统计数据</div>

			<template v-else>
				<!-- 指标卡 -->
				<div class="fin-cards">
					<div class="fin-card">
						<div class="fc-label">交易笔数</div>
						<div class="fc-value">{{ overview.txCount || 0 }}</div>
					</div>
					<div class="fin-card fc-primary">
						<div class="fc-label">卖出 U 总量</div>
						<div class="fc-value">{{ formatNum(overview.sellU) }}</div>
					</div>
					<div class="fin-card fc-success">
						<div class="fc-label">成交笔数</div>
						<div class="fc-value">{{ overview.finishedCount || 0 }}</div>
					</div>
					<div class="fin-card fc-info">
						<div class="fc-label">取消/关闭</div>
						<div class="fc-value">{{ overview.cancelCount || 0 }}</div>
					</div>
					<div class="fin-card fc-warning">
						<div class="fc-label">申诉中</div>
						<div class="fc-value">{{ overview.appealCount || 0 }}</div>
					</div>
				</div>
				<div v-if="overview.truncated" class="fin-truncated">
					数据量较大，统计仅基于最近部分订单，可缩小日期范围获取精确结果。
				</div>

				<!-- 按天统计（队长每日记账：法币结算为人民币） -->
				<div class="fin-section">
					<div class="fs-title">
						按天统计
						<span class="fs-sub">法币结算：人民币(CNY) · 卖出 U</span>
						<el-button size="mini" icon="el-icon-download" :disabled="!daily.length"
							class="fs-daily-export" @click="exportDailyCsv">导出按天CSV</el-button>
					</div>
					<el-table :data="daily" size="small" empty-text="暂无按天数据" v-loading="dailyLoading">
						<el-table-column prop="date" label="日期" width="120"></el-table-column>
						<el-table-column prop="txCount" label="交易笔数" width="100"></el-table-column>
						<el-table-column prop="finishedCount" label="成交笔数" width="100"></el-table-column>
						<el-table-column prop="cancelCount" label="取消/关闭" width="100"></el-table-column>
						<el-table-column label="卖出U总量" min-width="120">
							<template slot-scope="s">{{ formatNum(s.row.sellU) }}</template>
						</el-table-column>
						<el-table-column label="卖出人民币(元)" min-width="130">
							<template slot-scope="s">{{ formatNum(s.row.sellFiat) }}</template>
						</el-table-column>
					</el-table>
				</div>

				<!-- 收款渠道分布 -->
				<div class="fin-section">
					<div class="fs-title">收款渠道分布</div>
					<div class="fin-channels">
						<div class="fch-item fch-wechat">
							<span class="fch-name">微信</span>
							<span class="fch-count">{{ channels.wechatCount || 0 }} 笔</span>
							<span class="fch-amount">{{ formatNum(channels.wechatAmount) }}</span>
						</div>
						<div class="fch-item fch-ali">
							<span class="fch-name">支付宝</span>
							<span class="fch-count">{{ channels.aliCount || 0 }} 笔</span>
							<span class="fch-amount">{{ formatNum(channels.aliAmount) }}</span>
						</div>
					</div>
				</div>

				<!-- 待确认回款 -->
				<div class="fin-section">
					<div class="fs-title">
						待确认回款
						<span class="fs-refresh el-icon-refresh" title="刷新" @click="loadPending"></span>
					</div>
					<el-table :data="pending" size="small" empty-text="暂无待确认回款" v-loading="pendingLoading">
						<el-table-column prop="orderTxid" label="订单号" min-width="150" show-overflow-tooltip></el-table-column>
						<el-table-column label="金额" width="130">
							<template slot-scope="s">{{ s.row.amount }} {{ s.row.fiat || 'CNY' }}</template>
						</el-table-column>
						<el-table-column label="渠道" width="80">
							<template slot-scope="s">{{ channelText(s.row.payChannel) }}</template>
						</el-table-column>
						<el-table-column prop="salespersonName" label="业务员" width="110"></el-table-column>
						<el-table-column label="操作" width="120">
							<template slot-scope="s">
								<el-button type="success" size="mini" @click="confirmReceived(s.row)">确认收到</el-button>
							</template>
						</el-table-column>
					</el-table>
				</div>

				<!-- 订单明细 -->
				<div class="fin-section">
					<div class="fs-title">
						订单明细
						<el-select v-model="orderStatus" placeholder="全部状态" size="mini" clearable
							class="fs-status" @change="loadOrders(1)">
							<el-option v-for="s in statusOptions" :key="s.value" :label="s.label" :value="s.value"></el-option>
						</el-select>
						<el-radio-group v-model="exportDateRange" size="mini" class="fs-export-range">
							<el-radio-button label="today">今天</el-radio-button>
							<el-radio-button label="yesterday">昨天</el-radio-button>
							<el-radio-button label="7days">最近7天</el-radio-button>
							<el-radio-button label="30days">最近30天</el-radio-button>
						</el-radio-group>
						<el-button size="mini" icon="el-icon-download" :loading="exportingDailyDetail"
							:disabled="!orders.length" @click="exportDailyDetailCsv">按天导出(汇总+明细)</el-button>
					</div>
					<el-table :data="orders" size="small" empty-text="暂无订单" v-loading="ordersLoading">
						<el-table-column prop="timest" label="时间" width="160"></el-table-column>
						<el-table-column label="方向" width="70">
							<template slot-scope="s">{{ isBuy(s.row) ? '买入' : '卖出' }}</template>
						</el-table-column>
						<el-table-column label="状态" width="90">
							<template slot-scope="s">
								<span :class="'st-' + statusKey(s.row.status)">{{ statusText(s.row.status) }}</span>
							</template>
						</el-table-column>
						<el-table-column prop="amount" label="数量(U)" width="110"></el-table-column>
						<el-table-column prop="rate" label="出售单价" width="100"></el-table-column>
						<el-table-column label="法币" width="120">
							<template slot-scope="s">{{ s.row.total }} {{ s.row.currency_type || '' }}</template>
						</el-table-column>
						<el-table-column label="对手方" min-width="120" show-overflow-tooltip>
							<template slot-scope="s">
								<span v-if="isBlacklisted(s.row.its_realname || s.row.its_nick)" class="blacklist-hit">
									⚠️ {{ s.row.its_realname || s.row.its_nick }}
									<span class="blacklist-tip">黑名单·谨慎接待</span>
								</span>
								<span v-else>{{ s.row.its_realname || s.row.its_nick || '-' }}</span>
							</template>
						</el-table-column>
						<el-table-column label="业务员" width="100">
							<template slot-scope="s">{{ s.row.bizSalespersonName || '-' }}</template>
						</el-table-column>
						<el-table-column label="收款方式" width="90">
							<template slot-scope="s">{{ channelText(s.row.bizPayChannel) }}</template>
						</el-table-column>
						<el-table-column label="是否回款" width="110" align="center">
							<template slot-scope="s">
								<span v-if="rkState(s.row) === 'yes'" class="rk-yes"><i class="el-icon-success"></i> 已回款</span>
								<span v-else-if="rkState(s.row) === 'offline'" class="rk-yes"><i class="el-icon-success"></i> 已回款(线下)</span>
								<span v-else-if="rkState(s.row) === 'na'" class="rk-na">-</span>
								<!-- 买入单=财务付款买币,无回款概念:已完成=已付款,进行中=待财务付款 -->
								<span v-else-if="rkState(s.row) === 'pay'" class="rk-yes"><i class="el-icon-success"></i> 财务已付款</span>
								<span v-else-if="rkState(s.row) === 'paywait'" class="rk-wait"><i class="el-icon-time"></i> 待财务付款</span>
								<span v-else class="rk-no"><i class="el-icon-error"></i> 未回款</span>
							</template>
						</el-table-column>
						<el-table-column label="操作" width="90">
							<template slot-scope="s">
								<el-button type="text" size="mini" @click="openDetail(s.row)">详情</el-button>
							</template>
						</el-table-column>
					</el-table>
					<div class="fin-pager">
						<el-pagination background layout="prev, pager, next, total" :total="ordersTotal"
							:page-size="pageSize" :current-page="pageNo" @current-change="loadOrders"></el-pagination>
					</div>
				</div>
			</template>

			<!-- 红线提示 -->
			<div class="fin-redline">
				红线：本页仅展示芝麻(Gate)只读数据与本地标记，放行/确认收款均需在芝麻官方后台人工完成。
			</div>
		</template>

		<!-- 订单详情弹窗 -->
		<el-dialog title="订单详情" :visible.sync="detailDialog.visible" width="480px" append-to-body>
			<div v-loading="detailDialog.loading" class="fin-detail">
				<div v-if="detailRows.length" class="fd-list">
					<div class="fd-row" v-for="r in detailRows" :key="r.key">
						<span class="fd-key">{{ r.label }}</span>
						<span class="fd-val">{{ r.value }}</span>
					</div>
				</div>
				<div v-else class="fin-empty">暂无详情</div>
			</div>
		</el-dialog>
	</div>
</template>

<script>
import bizApi from '../api/bizApi.js';

const STATUS_TEXT = {
	finished: '已完成', cancel: '已取消', sclosed: '系统关闭', appeal: '申诉中',
	paid: '已付款', unpaid: '未付款', wait: '等待中'
};
const CHANNEL = { 1: '微信', 2: '支付宝' };

// 订单详情字段中文标签（与管理后台保持一致）
const ORD_DETAIL_LABELS = {
	orderid: '订单号', txid: '交易ID', status: '订单状态', state: '状态码',
	order_type: '订单类型', is_sell: '买卖方向', rate: '出售单价(1U=?元)', amount: '数量(U)',
	total: '订单金额(元)', payable_total: '应付金额(元)', currency_type: '数字货币',
	want_type: '法币', symbol: '货币符号', usdt_left_guarantee: '剩余保证金',
	timest: '下单时间', timest_paid: '付款时间', last_pay_time: '最后付款时间',
	cancel_time: '取消时间', dispute_time: '申诉时间', countdown: '倒计时(秒)',
	remain_pay_time: '剩余付款(秒)', its_realname: '对方实名', its_nickname: '对方昵称',
	its_uid: '对方UID', its_tier: '对方等级', own_realname: '本方实名',
	wename: '收款方式', sel_paytype: '选用支付方式', supported_pay_types: '支持支付方式',
	bankname: '银行名称', bankbranch: '支行', bank_holder_realname: '开户人',
	aliname: '支付宝姓名', payment_voucher_url: '付款凭证', payment_review_state: '付款审核状态',
	is_taker: '是否吃单方', is_black: '是否黑名单', is_risk_user: '风险用户',
	is_follow: '是否关注', is_online: '对方在线', have_traded: '是否交易过',
	cancelable: '可取消', in_appeal: '申诉中', seller_confirm: '卖家已确认',
	complete_number: '对方完成单数', complete_rate_month: '本月完成率(%)',
	confirmation_use_time_month: '本月确认用时', reason_id: '取消原因ID',
	reason_desc: '取消原因', message: '留言', trade_tips: '交易提示', score: '评分'
};
const ORD_DETAIL_ORDER = ['orderid', 'txid', 'status', 'is_sell', 'order_type', 'rate', 'amount', 'total', 'payable_total', 'currency_type', 'want_type', 'symbol', 'timest', 'timest_paid', 'last_pay_time', 'cancel_time', 'countdown', 'remain_pay_time', 'its_realname', 'its_nickname', 'its_uid', 'its_tier', 'own_realname', 'wename', 'sel_paytype', 'supported_pay_types', 'bankname', 'bankbranch', 'bank_holder_realname', 'aliname', 'payment_review_state', 'payment_voucher_url', 'complete_number', 'complete_rate_month', 'confirmation_use_time_month', 'seller_confirm', 'cancelable', 'in_appeal', 'reason_desc', 'message', 'trade_tips', 'usdt_left_guarantee', 'is_taker', 'is_black', 'is_risk_user', 'is_follow', 'is_online', 'have_traded', 'score', 'state'];
// 订单类型枚举（真实订单均为1=普通订单；未知值原样标注）
const ORDER_TYPE_TEXT = { 1: '普通订单' };
// 时间戳字段（epoch 秒）
const TS_KEYS = ['timest', 'timest_paid', 'last_pay_time', 'cancel_time', 'dispute_time'];
// 是/否 字段
const YN_KEYS = ['is_taker', 'is_black', 'is_risk_user', 'is_follow', 'is_online', 'have_traded', 'cancelable', 'in_appeal', 'seller_confirm', 'is_freeze_guarantee', 'show_wechat', 'show_ali', 'show_bank', 'show_others', 'use_coupon', 'hide_payment'];

export default {
	name: 'finance',
	data() {
		return {
			merchants: [],
			merchantId: '',
			closeRates: {},
			closeRateTimer: null,
			dateRange: [],
			overview: {},
			channels: {},
			overviewLoading: false,
			// 待确认回款
			pending: [],
			pendingLoading: false,
			// 订单明细
			orders: [],
			ordersTotal: 0,
			pageNo: 1,
			pageSize: 20,
			orderStatus: '',
			ordersLoading: false,
			statusOptions: [
				{ value: 'finished', label: '已完成' },
				{ value: 'cancel', label: '已取消' },
				{ value: 'sclosed', label: '系统关闭' },
				{ value: 'appeal', label: '申诉中' }
			],
			// 按天统计（队长每日记账）
			daily: [],
			dailyLoading: false,
			// 买家黑名单姓名集合（仅提示，不影响订单流转）
			blacklistSet: {},
			detailDialog: { visible: false, loading: false, data: null },
			exportingDailyDetail: false,
			exportDateRange: 'today',
			// 客情统计（业务员群内填报汇总）
			kq: {
				period: 'day',
				range: [],
				salespersonId: '',
				salesOptions: [],
				stats: {},
				fields: [],
				buckets: [],
				statsLoading: false,
				chartMetric: '__count',
				reports: [],
				reportsTotal: 0,
				reportsPageNo: 1,
				reportsPageSize: 20,
				reportsLoading: false,
				inited: false
			}
		};
	},
	computed: {
		isFinance() {
			return this.userStore && this.userStore.isBizFinance;
		},
		// 订单详情行：仅渲染有中文标签的字段，按管理后台顺序，值已中文化
		detailRows() {
			const obj = this.detailDialog.data;
			if (!obj || typeof obj !== 'object') return [];
			const rows = [];
			ORD_DETAIL_ORDER.forEach((k) => {
				if (obj[k] === undefined || !ORD_DETAIL_LABELS[k]) return;
				rows.push({ key: k, label: ORD_DETAIL_LABELS[k], value: this.fmtDetailVal(k, obj[k]) });
			});
			return rows;
		},
		// 有数值合计的客情字段(姓名/备注等文本字段不参与分析)
		kqNumericFields() {
			const sums = this.kq.stats.totalSums || {};
			return (this.kq.fields || []).filter(f => sums[f.id] !== undefined);
		},
		kqBucketLabel() {
			return { day: '日期', week: '周', month: '月份' }[this.kq.period] || '周期';
		},
		// 柱状图数据：按当前指标缩放到最大 90px
		kqChartBars() {
			const metric = this.kq.chartMetric;
			const bars = (this.kq.buckets || []).map(b => {
				let v = metric === '__count' ? (b.reportCount || 0) : Number((b.sums || {})[metric] || 0);
				if (isNaN(v)) v = 0;
				return { bucket: b.bucket, value: v };
			});
			const max = Math.max.apply(null, [1].concat(bars.map(b => b.value)));
			bars.forEach(b => { b.height = Math.max(2, Math.round(b.value / max * 90)); });
			return bars;
		}
	},
	methods: {
		async initPage() {
			if (this.isFinance) {
				this.loadMerchants();
				this.loadBlacklist();
			} else {
				try {
					await this.userStore.loadBizRole();
				} catch (e) {
					console.error('[Finance] retry loadBizRole failed', e);
				}
				if (this.isFinance) {
					this.loadMerchants();
					this.loadBlacklist();
				}
			}
		},
		formatNum(v) {
			if (v === null || v === undefined || v === '') return '0';
			const n = Number(v);
			return isNaN(n) ? String(v) : n.toLocaleString('zh-CN', { maximumFractionDigits: 4 });
		},
		formatVal(v) {
			if (v === null || v === undefined) return '';
			if (typeof v === 'object') return JSON.stringify(v);
			return String(v);
		},
		channelText(c) { return CHANNEL[c] || '-'; },
		// 回款展示状态: 已取消订单不适用(na); 买入单走付款语义(pay/paywait); 财务已确认(yes); 无本地协作记录=历史订单已线下回款(offline); 否则未回款(no)
		rkState(o) {
			const k = this.statusKey(o.status);
			if (k === 'cancel' || k === 'sclosed') return 'na';
			// 买入单是财务直接付款买币,不存在"回款":已完成=付款已付,其余=待财务付款
			if (this.isBuy(o)) return k === 'finished' ? 'pay' : 'paywait';
			if (o.bizFinanceReceived) return 'yes';
			if (o.bizFlowStatus === null || o.bizFlowStatus === undefined) return 'offline';
			return 'no';
		},
		rkText(o) {
			return { na: '-', yes: '已回款', offline: '已回款(线下)', no: '未回款', pay: '财务已付款', paywait: '待财务付款' }[this.rkState(o)];
		},
		statusText(s) { return STATUS_TEXT[this.statusKey(s)] || (s || '-'); },
		statusKey(s) { return (s || '').toLowerCase(); },
		// epoch 秒 → 日期字符串（用本地时区 UTC+8，与芝麻后台一致）
		ordTsFmt(sec) {
			const d = new Date(sec * 1000);
			const p = (x) => (x < 10 ? '0' : '') + x;
			return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate()) + ' '
				+ p(d.getHours()) + ':' + p(d.getMinutes()) + ':' + p(d.getSeconds());
		},
		// 订单详情单字段值中文化
		fmtDetailVal(k, v) {
			if (v === null || v === undefined || v === '') return '—';
			if (Array.isArray(v)) return v.length ? v.join('、') : '—';
			if (TS_KEYS.indexOf(k) !== -1) {
				const n = Number(v);
				if (n > 0) return this.ordTsFmt(n);
				if (typeof v === 'string' && v.length >= 10) return v;
				return '—';
			}
			if (k === 'is_sell') return String(v) === '1' ? '卖出' : '买入';
			if (k === 'order_type') return ORDER_TYPE_TEXT[v] || ('类型' + v);
			if (k === 'status' || k === 'state') return this.statusText(String(v).toLowerCase());
			if (YN_KEYS.indexOf(k) !== -1) return (v == 1 || v === true || v === 'true') ? '是' : '否';
			if (v && typeof v === 'object') return JSON.stringify(v);
			return String(v);
		},
		isBuy(o) {
			if (o.type_buy !== undefined && o.type_buy !== null) return String(o.type_buy) === '1';
			if (o.is_sell !== undefined && o.is_sell !== null) return String(o.is_sell) === '0';
			return false;
		},
		params() {
			const p = { merchantId: this.merchantId };
			if (this.dateRange && this.dateRange.length === 2) {
				p.from = this.dateRange[0];
				p.to = this.dateRange[1];
			}
			return p;
		},
		loadMerchants() {
			bizApi.statsMerchants().then((list) => {
				this.merchants = list || [];
				if (this.merchants.length && !this.merchantId) {
					this.merchantId = this.merchants[0].id;
					this.onQuery();
				}
				this.loadCloseRates();
			}).catch(() => { });
		},
		// 商户名称后挂实时成交率(芝麻后台口径:成功放币÷(放币+取消)),30s轮询+后端逐笔终态刷新
		rateSuffix(id) {
			const r = this.closeRates[id];
			return r ? `（成交率${r.rate}%）` : '';
		},
		loadCloseRates() {
			if (!this.merchants.length) return;
			this.merchants.forEach((m) => {
				bizApi.statsCloseRate(m.id).then((d) => {
					if (d) this.$set(this.closeRates, m.id, d);
				}).catch(() => { });
			});
		},
		onQuery() {
			if (!this.merchantId) return;
			this.loadOverview();
			this.loadDaily();
			this.loadPending();
			this.loadOrders(1);
		},
		loadDaily() {
			if (!this.merchantId) return;
			this.dailyLoading = true;
			bizApi.statsDaily(this.params()).then((list) => {
				this.daily = list || [];
			}).catch(() => { }).finally(() => { this.dailyLoading = false; });
		},
		loadOverview() {
			this.overviewLoading = true;
			bizApi.statsOverview(this.params()).then((d) => {
				this.overview = d || {};
				this.channels = (d && d.channels) || {};
			}).catch(() => { }).finally(() => { this.overviewLoading = false; });
		},
		loadPending() {
			if (!this.merchantId) return;
			this.pendingLoading = true;
			// flowStatus=4: 业务员已转款，待财务确认收到
			bizApi.receiptPage({ flowStatus: 4, pageNo: 1, pageSize: 100 }).then((d) => {
				const list = (d && d.list) || [];
				this.pending = list.filter(r => r.merchantId === this.merchantId);
			}).catch(() => { }).finally(() => { this.pendingLoading = false; });
		},
		confirmReceived(row) {
			this.$confirm('确认已收到业务员转来的款项？确认后该订单完结。', '确认收到回款', {
				confirmButtonText: '确认收到', cancelButtonText: '取消', type: 'success'
			}).then(() => {
				return bizApi.financeReceived(row.id);
			}).then(() => {
				this.$message.success('订单已完结');
				this.loadPending();
			}).catch(() => { });
		},
		loadOrders(page) {
			if (!this.merchantId) return;
			this.pageNo = page || 1;
			this.ordersLoading = true;
			const p = { merchantId: this.merchantId, page: this.pageNo, pageSize: this.pageSize };
			if (this.orderStatus) p.status = this.orderStatus;
			bizApi.statsOrders(p).then((d) => {
				this.orders = (d && d.list) || [];
				this.ordersTotal = (d && Number(d.count)) || 0;
			}).catch(() => { }).finally(() => { this.ordersLoading = false; });
		},
		// 买家黑名单：拉取启用中的姓名，建集合供订单列表标红比对
		loadBlacklist() {
			bizApi.blacklistNames().then((names) => {
				const set = {};
				(names || []).forEach((n) => { const k = String(n == null ? '' : n).trim(); if (k) set[k] = 1; });
				this.blacklistSet = set;
			}).catch(() => { });
		},
		isBlacklisted(name) {
			const k = String(name == null ? '' : name).trim();
			return k !== '' && this.blacklistSet[k] === 1;
		},
		openDetail(row) {
			this.detailDialog.visible = true;
			this.detailDialog.loading = true;
			this.detailDialog.data = null;
			bizApi.orderDetail(this.merchantId, row.txid).then((d) => {
				// 解包 Gate 只读响应
				let data = d;
				if (d && typeof d === 'object' && d.data && typeof d.data === 'object') data = d.data;
				this.detailDialog.data = data;
			}).catch(() => { }).finally(() => { this.detailDialog.loading = false; });
		},
		exportCsv() {
			if (!this.orders.length) return;
			const headers = ['时间', '方向', '状态', '数量(U)', '出售单价', '法币金额', '法币', '对手方', '业务员', '收款方式', '是否回款', '订单号'];
			const rows = this.orders.map(o => [
				o.timest || '',
				this.isBuy(o) ? '买入' : '卖出',
				this.statusText(o.status),
				o.amount || '',
				o.rate || '',
				o.total || '',
				o.currency_type || '',
				o.its_realname || o.its_nick || '',
				o.bizSalespersonName || '',
				this.channelText(o.bizPayChannel),
				this.rkText(o),
				o.txid || ''
			]);
			const escape = (val) => {
				const s = String(val == null ? '' : val);
				return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
			};
			const lines = [headers.map(escape).join(','), ...rows.map(r => r.map(escape).join(','))];
			// 加 BOM 以便 Excel 正确识别 UTF-8
			const blob = new Blob(['\ufeff' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = `订单明细_${this.merchantId}_${new Date().toISOString().slice(0, 10)}.csv`;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(url);
		},
		exportDailyCsv() {
			if (!this.daily.length) return;
			const headers = ['日期', '交易笔数', '成交笔数', '取消/关闭', '卖出U总量', '卖出人民币(元)'];
			const rows = this.daily.map(o => [
				o.date || '', o.txCount || 0, o.finishedCount || 0, o.cancelCount || 0,
				o.sellU || 0, o.sellFiat || 0
			]);
			const escape = (val) => {
				const s = String(val == null ? '' : val);
				return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
			};
			const lines = [headers.map(escape).join(','), ...rows.map(r => r.map(escape).join(','))];
			const blob = new Blob(['\ufeff' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = `按天统计_${this.merchantId}_${new Date().toISOString().slice(0, 10)}.csv`;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(url);
		},
		// 计算导出日期范围 [fromYYYYMMDD, toYYYYMMDD]
		exportDateBounds() {
			const fmt = (d) => d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2);
			const today = new Date();
			switch (this.exportDateRange) {
				case 'yesterday': {
					const y = new Date(today.getTime() - 86400000);
					return [fmt(y), fmt(y)];
				}
				case '7days': {
					const from = new Date(today.getTime() - 6 * 86400000);
					return [fmt(from), fmt(today)];
				}
				case '30days': {
					const from = new Date(today.getTime() - 29 * 86400000);
					return [fmt(from), fmt(today)];
				}
				default: // today
					return [fmt(today), fmt(today)];
			}
		},
		// 按天导出(汇总+明细): 拉取全部分页订单, 按日期分组, 每组先输出汇总行再输出明细
		exportDailyDetailCsv() {
			if (!this.merchantId) return;
			this.exportingDailyDetail = true;
			const [fromDate, toDate] = this.exportDateBounds();
			const allOrders = [];
			const pageSize = 100;
			const maxPages = 30;
			const fetchPage = (page) => {
				const p = { merchantId: this.merchantId, page, pageSize };
				if (this.orderStatus) p.status = this.orderStatus;
				return bizApi.statsOrders(p).then((d) => {
					const list = (d && d.list) || [];
					allOrders.push(...list);
					const total = (d && Number(d.count)) || 0;
					if (list.length < pageSize || page >= maxPages || allOrders.length >= total) {
						return allOrders;
					}
					return fetchPage(page + 1);
				});
			};
			fetchPage(1).then(() => {
				// 按所选日期范围过滤
				const filtered = allOrders.filter(o => {
					const date = String(o.timest || '').substring(0, 10);
					return date >= fromDate && date <= toDate;
				});
				if (!filtered.length) {
					this.$message.warning(`${fromDate}~${toDate} 暂无订单数据`);
					return;
				}
				// 按日期分组 (timest 前10字符即日期)
				const groups = {};
				filtered.forEach(o => {
					const date = String(o.timest || '').substring(0, 10);
					if (!groups[date]) groups[date] = [];
					groups[date].push(o);
				});
				const dates = Object.keys(groups).sort((a, b) => b.localeCompare(a));
				const headers = ['日期', '时间', '方向', '状态', '数量(U)', '出售单价', '法币金额', '法币', '对手方', '业务员', '收款方式', '是否回款', '订单号'];
				const escape = (val) => {
					const s = String(val == null ? '' : val);
					return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
				};
				const lines = [headers.map(escape).join(',')];
				dates.forEach(date => {
					const ords = groups[date];
					let txCount = ords.length, finished = 0, cancel = 0, sellU = 0, sellFiat = 0, received = 0, finishedSell = 0;
					ords.forEach(o => {
						const st = (o.status || '').toLowerCase();
						if (st === 'finished') {
							finished++;
							if (!this.isBuy(o)) { finishedSell++; sellU += Number(o.amount) || 0; sellFiat += Number(o.total) || 0; }
						} else if (st === 'cancel' || st === 'sclosed') { cancel++; }
						const rk = this.rkState(o);
						if (rk === 'yes' || rk === 'offline') received++;
					});
					// 已回款分母只算卖出成交单(买入单是财务付款,无回款概念)
					const summary = `【汇总】 交易${txCount}笔 | 成交${finished}笔 | 取消${cancel}笔 | 卖出U:${this.formatNum(sellU)} | 卖出人民币:${this.formatNum(sellFiat)} | 已回款:${received}/${finishedSell}`;
					lines.push([escape(date), escape(summary), '', '', '', '', '', '', '', '', '', '', ''].join(','));
					ords.forEach(o => {
						lines.push([
							'', o.timest || '', this.isBuy(o) ? '买入' : '卖出', this.statusText(o.status),
							o.amount || '', o.rate || '', o.total || '', o.currency_type || '',
							o.its_realname || o.its_nick || '', o.bizSalespersonName || '',
							this.channelText(o.bizPayChannel), this.rkText(o), o.txid || ''
						].map(escape).join(','));
					});
				});
				const blob = new Blob(['\ufeff' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
				const url = URL.createObjectURL(blob);
				const a = document.createElement('a');
				a.href = url;
				a.download = `按天订单导出_${fromDate}_${toDate}.csv`;
				document.body.appendChild(a);
				a.click();
				document.body.removeChild(a);
				URL.revokeObjectURL(url);
			}).catch(() => {
				this.$message.error('导出失败，请重试');
			}).finally(() => { this.exportingDailyDetail = false; });
		},
		// ---------- 客情统计 ----------
		kqInit() {
			if (this.kq.inited) return;
			this.kq.inited = true;
			// 默认最近30天
			const fmt = (d) => d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2);
			const to = new Date();
			const from = new Date(to.getTime() - 29 * 86400000);
			this.kq.range = [fmt(from), fmt(to)];
			bizApi.salespersons().then((list) => {
				this.kq.salesOptions = list || [];
			}).catch(() => { });
			this.kqOnQuery();
		},
		kqParams() {
			const p = {};
			if (this.kq.range && this.kq.range.length === 2) {
				p.from = this.kq.range[0];
				p.to = this.kq.range[1];
			}
			if (this.kq.salespersonId) p.salespersonId = this.kq.salespersonId;
			return p;
		},
		kqOnQuery() {
			this.loadKqStats();
			this.loadKqReports(1);
		},
		loadKqStats() {
			this.kq.statsLoading = true;
			const p = Object.assign({ period: this.kq.period }, this.kqParams());
			bizApi.kqStats(p).then((d) => {
				this.kq.stats = d || {};
				this.kq.fields = (d && d.fields) || [];
				this.kq.buckets = (d && d.buckets) || [];
			}).catch(() => { }).finally(() => { this.kq.statsLoading = false; });
		},
		loadKqReports(page) {
			this.kq.reportsPageNo = page || 1;
			this.kq.reportsLoading = true;
			const p = Object.assign({ pageNo: this.kq.reportsPageNo, pageSize: this.kq.reportsPageSize }, this.kqParams());
			bizApi.kqReports(p).then((d) => {
				this.kq.reports = (d && d.list) || [];
				this.kq.reportsTotal = (d && Number(d.total)) || 0;
			}).catch(() => { }).finally(() => { this.kq.reportsLoading = false; });
		},
		// 汇总表单字段列：无数值合计或0的字段显示 -
		kqSumVal(row, fieldId) {
			const v = (row.sums || {})[fieldId];
			return v === undefined || v === null || Number(v) === 0 ? '-' : this.formatNum(v);
		},
		// 明细行字段值：解析 content JSON
		kqContentVal(row, fieldId) {
			if (!row._contentMap) {
				let map = {};
				try {
					map = JSON.parse(row.content) || {};
				} catch (e) { /* ignore */ }
				row._contentMap = map;
			}
			const v = row._contentMap[String(fieldId)];
			// 未填写与填0均展示为 -
			return v === undefined || v === null || v === '' || String(v).trim() === '0' ? '-' : v;
		},
		kqCsvDownload(headers, rows, filename) {
			const escape = (val) => {
				const s = String(val == null ? '' : val);
				return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
			};
			const lines = [headers.map(escape).join(','), ...rows.map(r => r.map(escape).join(','))];
			const blob = new Blob(['\ufeff' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(url);
		},
		exportKqSummaryCsv() {
			if (!this.kq.buckets.length) return;
			const headers = [this.kqBucketLabel, '填报数', '填报人数'].concat(this.kq.fields.map(f => f.name));
			const rows = this.kq.buckets.map(b => [b.bucket, b.reportCount || 0, b.salespersonCount || 0]
				.concat(this.kq.fields.map(f => this.kqSumVal(b, f.id))));
			const tag = { day: '按日', week: '按周', month: '按月' }[this.kq.period] || this.kq.period;
			this.kqCsvDownload(headers, rows, `客情统计汇总_${tag}_${new Date().toISOString().slice(0, 10)}.csv`);
		},
		exportKqDetailCsv() {
			// 全量拉取当前筛选下的明细(后端单页上限500)
			const p = Object.assign({ pageNo: 1, pageSize: 500 }, this.kqParams());
			bizApi.kqReports(p).then((d) => {
				const list = (d && d.list) || [];
				if (!list.length) return;
				const headers = ['日期', '业务员', '客勤群'].concat(this.kq.fields.map(f => f.name));
				const rows = list.map(r => [r.reportDate || '', r.salespersonName || '', r.kqGroupName || '']
					.concat(this.kq.fields.map(f => {
						const v = this.kqContentVal(r, f.id);
						return v === '-' ? '' : v;
					})));
				this.kqCsvDownload(headers, rows, `客情填报明细_${new Date().toISOString().slice(0, 10)}.csv`);
			}).catch(() => { });
		}
	},
	watch: {
		// bizRole 由异步 me() 写入 userStore，可能晚于本组件 mounted。
		// 若挂载时 isFinance 尚为 false，mounted 里的加载会被跳过；
		// 待 bizRole 就位使 isFinance 变 true 时，需补加载一次，避免商户下拉空「无数据」。
		isFinance(val) {
			if (val && !this.merchants.length) {
				this.loadMerchants();
				this.loadBlacklist();
			}
		}
	},
	mounted() {
		this.initPage();
		this.closeRateTimer = setInterval(() => this.loadCloseRates(), 30000);
	},
	beforeDestroy() {
		if (this.closeRateTimer) {
			clearInterval(this.closeRateTimer);
			this.closeRateTimer = null;
		}
	}
}
</script>

<style scoped lang="scss">
.blacklist-hit {
	color: #f56c6c;
	font-weight: 700;
	display: inline-flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 4px;
}

.blacklist-tip {
	display: inline-block;
	padding: 0 6px;
	font-size: 11px;
	font-weight: 600;
	color: #fff;
	background: #f56c6c;
	border-radius: 8px;
	line-height: 18px;
}

.finance-page {
	height: 100%;
	overflow-y: auto;
	padding: 16px 20px;
	box-sizing: border-box;
	background: var(--im-bg-color, #f5f6f8);
}

.fin-forbidden {
	height: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	color: var(--im-text-color-light, #909399);

	i { font-size: 48px; margin-bottom: 12px; }
	.fb-title { font-size: 18px; font-weight: 600; }
	.fb-tip { font-size: 13px; margin-top: 6px; }
}

.fin-toolbar {
	display: flex;
	align-items: center;
	gap: 12px;
	flex-wrap: wrap;
	margin-bottom: 16px;

	.fin-title { font-size: 18px; font-weight: 700; margin-right: 8px; }
	.fin-merchant { width: 180px; }
	.fin-date { width: 260px; }
}

.fin-empty {
	padding: 60px 0;
	text-align: center;
	color: var(--im-text-color-lighter, #c0c4cc);
}

.fin-cards {
	display: flex;
	gap: 14px;
	flex-wrap: wrap;
	margin-bottom: 10px;

	.fin-card {
		flex: 1;
		min-width: 130px;
		background: var(--im-bg-color-light, #fff);
		border-radius: 12px;
		padding: 16px 18px;
		box-shadow: 0 1px 6px rgba(0, 0, 0, 0.05);

		.fc-label { font-size: 13px; color: var(--im-text-color-light, #909399); }
		.fc-value { font-size: 26px; font-weight: 700; margin-top: 8px; color: var(--im-text-color, #303133); }
	}

	.fc-primary .fc-value { color: #409eff; }
	.fc-success .fc-value { color: #67c23a; }
	.fc-info .fc-value { color: #909399; }
	.fc-warning .fc-value { color: #e6a23c; }
}

.fin-truncated {
	font-size: 12px;
	color: #e6a23c;
	margin-bottom: 10px;
}

.fin-section {
	background: var(--im-bg-color-light, #fff);
	border-radius: 12px;
	padding: 14px 16px;
	margin-bottom: 14px;
	box-shadow: 0 1px 6px rgba(0, 0, 0, 0.05);

	.fs-title {
		font-size: 15px;
		font-weight: 600;
		margin-bottom: 12px;
		display: flex;
		align-items: center;
		gap: 10px;
	}

	.fs-refresh {
		cursor: pointer;
		font-size: 15px;
		color: var(--im-text-color-light, #909399);
		&:hover { color: #409eff; }
	}

	.fs-status { width: 120px; margin-left: auto; }
	.fs-export-range { margin-left: 8px; }

	.fs-sub { font-size: 12px; font-weight: 400; color: var(--im-text-color-light, #909399); }
	.fs-daily-export { margin-left: auto; }
}

.fin-channels {
	display: flex;
	gap: 14px;

	.fch-item {
		flex: 1;
		display: flex;
		align-items: center;
		gap: 12px;
		padding: 12px 16px;
		border-radius: 10px;

		.fch-name { font-weight: 600; }
		.fch-count { color: var(--im-text-color-light, #909399); font-size: 13px; }
		.fch-amount { margin-left: auto; font-size: 18px; font-weight: 700; }
	}

	.fch-wechat { background: rgba(103, 194, 58, 0.1); .fch-amount { color: #67c23a; } }
	.fch-ali { background: rgba(64, 158, 255, 0.1); .fch-amount { color: #409eff; } }
}

.fin-pager {
	margin-top: 12px;
	text-align: right;
}

// 客情统计
.fin-kq {
	.kq-range { width: 240px; }
	.kq-sales { width: 130px; }
}

.kq-cards {
	display: flex;
	gap: 12px;
	flex-wrap: wrap;
	margin-bottom: 14px;

	.kq-card {
		min-width: 110px;
		padding: 10px 16px;
		border-radius: 10px;
		background: rgba(64, 158, 255, 0.06);

		.kc-label { font-size: 12px; color: var(--im-text-color-light, #909399); }
		.kc-value { font-size: 20px; font-weight: 700; margin-top: 4px; color: #409eff; }
	}
}

.kq-chart {
	margin-bottom: 14px;
	padding: 10px 12px;
	border-radius: 10px;
	background: rgba(0, 0, 0, 0.02);

	.kq-chart-head {
		display: flex;
		align-items: center;
		gap: 10px;
		margin-bottom: 8px;

		.kq-metric { width: 120px; }
	}

	.kq-bars {
		display: flex;
		align-items: flex-end;
		gap: 10px;
		min-height: 130px;
		overflow-x: auto;
		padding-bottom: 4px;

		.kq-bar-item {
			display: flex;
			flex-direction: column;
			align-items: center;
			gap: 4px;
			min-width: 48px;

			.kq-bar-val { font-size: 11px; color: var(--im-text-color-light, #909399); }

			.kq-bar {
				width: 22px;
				border-radius: 4px 4px 0 0;
				background: linear-gradient(180deg, #66b1ff, #409eff);
			}

			.kq-bar-label { font-size: 11px; color: var(--im-text-color-light, #909399); white-space: nowrap; }
		}
	}
}

.kq-detail-title {
	font-size: 14px;
	font-weight: 600;
	margin: 16px 0 10px;
}

.st-finished { color: #67c23a; }
.st-cancel, .st-sclosed { color: #909399; }
.st-appeal { color: #e6a23c; }
.st-paid { color: #409eff; }

.rk-yes { color: #67c23a; font-size: 13px; i { margin-right: 2px; } }
.rk-no { color: #f56c6c; font-size: 13px; i { margin-right: 2px; } }
.rk-na { color: #c0c4cc; font-size: 13px; }
.rk-wait { color: #e6a23c; font-size: 13px; i { margin-right: 2px; } }

.fin-redline {
	margin-top: 6px;
	padding: 10px 14px;
	font-size: 12px;
	line-height: 1.6;
	color: #e6a23c;
	background: rgba(230, 162, 60, 0.08);
	border-radius: 10px;
}

.fin-detail {
	max-height: 420px;
	overflow-y: auto;

	.fd-row {
		display: flex;
		padding: 7px 0;
		border-bottom: 1px solid rgba(0, 0, 0, 0.05);
		font-size: 13px;

		.fd-key { width: 140px; flex-shrink: 0; color: var(--im-text-color-light, #909399); }
		.fd-val { flex: 1; word-break: break-all; }
	}
}
</style>
